<template>
  <ValidationObserver ref="observer">
    <form ref="form" slot-scope="{ validate }" @submit.prevent="validate().then(submit)">
      <ion-card :class="{floating: !original_message}" style="margin-bottom: 0;">
        <ion-item color="primary" v-if="! $store.getters.authed && ! isReaction">
          <i18n path="ncrypt.write_message.application_invite_only" tag="ion-text">
            <template v-slot:contact>
              <a href="mailto:info@ncrypt.nl" style="color: white;">{{ $t('ncrypt.contact') }}</a>
            </template>
          </i18n>
        </ion-item>
        <monthly-message-limit/>
      </ion-card>
      <ion-card :class="{floating: !original_message, limitReached: $store.getters.monthlyMessageLimitExceeded}"
                style="margin-top: 0;">
        <ion-card-header>
          {{ $t("ncrypt.write_message.title") }}
        </ion-card-header>
        <ion-card-content>
          <ValidationProvider rules="required|email" :name="$t('ncrypt.write_message.recipient_to_label')"
                              v-slot="{ errors }">
            <ion-item>
              <ion-label position="floating">{{ $t('ncrypt.write_message.recipient_to_label') }}</ion-label>
              <ion-input v-model="message.recipient"
                         inputmode="email" type="email"
                         :value="message.recipient"
                         ref="updateRecipients"
                         @input="updateRecipients"
                         @ionBlur="clearContactResultsDelayed"
                         :color="errors.length ? 'danger' : ''"
                         :placeholder="$t('ncrypt.write_message.recipient_email_address_placeholder')" multiple="true">
              </ion-input>
            </ion-item>
            <!-- TODO: implement delay? https://github.com/charliekassel/vuejs-autocomplete/issues/66#issuecomment-574969344 -->
            <autocomplete
                v-if="$store.getters.authed && $store.getters.settings.contact_autofill.value"
                ref="autocomplete"
                method="post"
                :source="`${ $store.getters.api_url }/contacts/autocomplete-search?q=`"
                results-property="contacts"
                :results-display="formattedDisplay"
                credentials="include"
                @noResults="clearContactResults"
                @selected="contactSelected"/>
            <ion-item v-show="errors.length" lines="none" class="ion-no-padding">
              <ion-note class="error" slot="end">{{ errors[0] }}</ion-note>
            </ion-item>
          </ValidationProvider>
          <ValidationProvider rules="required|email" :name="$t('ncrypt.write_message.sender_from_label')"
                              v-slot="{ errors }" v-if="!$store.getters.authed">
            <ion-item>
              <ion-label position="floating">{{ $t('ncrypt.write_message.sender_from_label') }}</ion-label>
              <ion-input v-model="message.sender" inputmode="email" type="email" :value="message.sender" ref="sender"
                         @input="updateSender"
                         :color="errors.length ? 'danger' : ''"
                         :placeholder="$t('ncrypt.write_message.sender_email_address_placeholder')"/>
            </ion-item>
            <ion-item v-show="errors.length" lines="none" class="ion-no-padding">
              <ion-note class="error" slot="end">{{ errors[0] }}</ion-note>
            </ion-item>
          </ValidationProvider>
          <ValidationProvider rules="required|max:80" :name="$t('ncrypt.write_message.subject_label')"
                              v-slot="{ errors }">
            <ion-item>
              <ion-label position="floating">{{ $t('ncrypt.write_message.subject_label') }}</ion-label>
              <ion-input v-model="message.subject" :value="message.subject" ref="updateSubject" @input="updateSubject"
                         :color="errors.length ? 'danger' : ''"
                         :placeholder="$t('ncrypt.write_message.subject_placeholder')"/>
            </ion-item>
            <ion-item v-show="errors.length" lines="none" class="ion-no-padding">
              <ion-note class="error" slot="end">{{ errors[0] }}</ion-note>
            </ion-item>
          </ValidationProvider>
          <ValidationProvider rules="required" :name="$t('ncrypt.write_message.body_label')" v-slot="{ errors }">
            <ion-item>
              <ion-label position="floating">{{ $t('ncrypt.write_message.body_label') }}</ion-label>
              <ion-textarea v-model="message.body" :value="message.body" ref="updateBody" @input="updateBody"
                            :color="errors.length ? 'danger' : ''"
                            :placeholder="$t('ncrypt.write_message.body_placeholder')"/>
            </ion-item>
            <ion-item v-show="errors.length" lines="none" class="ion-no-padding">
              <ion-note class="error" slot="end">{{ errors[0] }}</ion-note>
            </ion-item>
          </ValidationProvider>
          <ion-item lines="none">
            <upload-attachment ref="uploader"/>
          </ion-item>
          <ion-item lines="none" v-show="vaultFiles.length" color="light" style="margin-bottom: 40px; width: 100%;">
            <ion-list lines="none" style="background:transparent;">
              <ion-label>{{ $t('ncrypt.write_message.vault_attached_files_label') }}</ion-label>
              <vault-file
                  :file="vaultFile"
                  v-for="vaultFile in vaultFiles"
                  v-bind:key="vaultFile.id"
                  :withDate="false"
                  :can-delete="true"
                  v-on:remove="detachVaultFile(vaultFile)"
              />
            </ion-list>
          </ion-item>

          <ion-item lines="none" class="ion-no-padding" style="margin-right: 65px;">
            <ion-checkbox :checked="notifySelfOnSentMessage ? 'true' : 'false'"
                          @ionChange="notifySelfOnSentMessage = $event.target.checked"
                          color="primary"
                          slot="start"
                          style="margin-right: 15px;"></ion-checkbox>
            <ion-label>
              {{ $t('ncrypt.write_message.notify_sent_message_checkbox_label') }}
            </ion-label>
          </ion-item>

          <br/>

          <ion-fab slot="end" vertical="bottom" horizontal="end" :style="isReaction ? 'margin-right: 60px;' : ''">
            <ion-fab-button @click="validate().then(submit)">
              <ion-icon ios='send' md="send"/>
            </ion-fab-button>
          </ion-fab>

          <!--<ion-note slot="end">Verzenden</ion-note>-->
        </ion-card-content>
      </ion-card>
    </form>
  </ValidationObserver>
</template>

<script>
  import moment from "moment";
  import {ApiError} from "../../errors/ApiError";
  import {ValidationObserver, ValidationProvider} from 'vee-validate';
  import RegisterModal from "../auth/RegisterModal";
  import LoginModal from "../auth/LoginModal";
  import {Notifier} from "../../services/Notifier";
  import UploadAttachment from "../UploadAttachment";
  import Autocomplete from 'vuejs-auto-complete'
  import {ADD_FLASH_MESSAGE, IS_UPLOADING_MUTATION} from "../../const/mutation-types";
  import {MessageType} from "../../const/MessageType";
  import {ErrorHandler} from "../../services/ErrorHandler";
  import MonthlyMessageLimit from "../ui-elements/MonthlyMessageLimit";
  import VaultFile from "../vault/VaultFIle";

  export default {
    name: "WriteMessage",
    components: {
      VaultFile,
      MonthlyMessageLimit,
      UploadAttachment,
      ValidationObserver,
      ValidationProvider,
      Autocomplete,
    },
    props: {
      type: {type: String, default: MessageType.ORIGINAL},
      original_message: {type: Object},
      recipientViewer: {type: Object},
    },
    computed: {
      senderEmail() {
        if (this.$store.getters.authed) {
          return this.$store.getters.user.email;
        }

        if (this.recipientViewer) {
          return this.recipientViewer.email;
        }

        return undefined;
      }
    },
    data() {
      const subject = (() => {
        if (!this.original_message) {
          return this.$route.params.subject || '';
        }

        if (this.type === MessageType.FORWARD) {
          return `FWD: ${this.original_message.subject}`;
        }

        if (this.type === MessageType.REPLY) {
          return `RE: ${this.original_message.subject}`;
        }

        return this.original_message.subject || '';
      })();

      const recipient = (() => {
        if (this.original_message && this.type === MessageType.REPLY) {
          const senderOfOriginalMessage = this.original_message.sender.email;
          if (senderOfOriginalMessage === this.senderEmail) {
            return this.original_message.recipients.first().email;
          }
          return senderOfOriginalMessage;
        }

        return this.$route.params.to || '';
      })();

      const vaultFiles = (this.$route.query.vaultFiles || []).filter(item => item.id);

      return {
        modal: null,
        isReaction: this.type !== MessageType.ORIGINAL,
        vaultFiles,
        ready_to_send: false,
        password_confirm: null,
        password: null,
        hasAccount: false,
        loader: null,
        slideOpts: {},
        unwatch: null,
        notifySelfOnSentMessage: this.$store.getters.notifySelfOnSentMessage,
        message: {
          id: undefined,
          sent_at: undefined,
          recipient,
          subject,
          body: '',
          type: this.type,
          original_message_id: this.original_message ? this.original_message.id : undefined,
          sender: this.senderEmail,
        },
      }
    },

    destroyed() {
      this.unwatch();
    },
    mounted() {
      this.unwatch = this.$store.watch(
          (state) => state.user,
          (value) => {
            if (value && this.ready_to_send) {
              this.$ionic.loadingController.dismiss();
              this.modal.dismiss();

              this.send();
            }
          }
      );

      if (!this.message.sender) {
        this.message.sender = this.senderEmail;
      }

      this.$nextTick(async () => {
        setTimeout(async () => {
          if (!this.message.recipient) {
            // await this.$refs.updateRecipients.setFocus();
          } else if (!this.message.subject) {
            // await this.$refs.updateSubject.setFocus();
          } else {
            // await this.$refs.updateBody.setFocus();
          }
        }, 700);
      });
    },
    methods: {
      submit() {
        if (!this.$refs.uploader.isValid()) {
          new ApiError(
              this.$t('ncrypt.errors.attachment_validation_error_body'),
              false,
              this.$t('ncrypt.errors.attachment_validation_error_title')
          );
          return;
        }

        if (this.$store.getters.authed) {
          return this.send();
        }

        this.ready_to_send = true;
        this.authenticate();
      },
      detachVaultFile(file) {
        this.vaultFiles = this.vaultFiles.filter(item => item.id !== file.id);
      },
      hasAccountCheck() {
        return new Promise((resolve, reject) => {
          this.$http.post('/account-check', {
            email: this.message.sender,
            hasCompletedRegistration: true,
          })
              .then(({data}) => resolve(data.exists))
              .catch(reason => reject(reason));
        });
      },
      async authenticate() {
        this.loader = await this.$ionic.loadingController.create({duration: 10000});

        await this.loader.present();

        const hasAccount = await this.hasAccountCheck();

        await this.loader.dismiss();

        if (hasAccount) {
          return this.openLoginModal();
        }
        return this.openRegisterModal();
      },
      async openRegisterModal() {
        this.modal = await this.$ionic.modalController.create({
          component: RegisterModal,
          componentProps: {
            parent: this,
            propsData: {
              intro: this.$t('ncrypt.write_message.register_before_sending_intro'),
              initialEmail: this.message.sender,
            },
          },
        });
        await this.modal.present();
      },
      async openLoginModal() {
        this.modal = await this.$ionic.modalController.create({
          component: LoginModal,
          componentProps: {
            parent: this,
            propsData: {
              intro: this.$t('ncrypt.write_message.login_before_sending_intro'),
              initialEmail: this.message.sender,
              submitText: this.$t('ncrypt.write_message.login_before_sending_submit_button'),
            },
          },
        });
        await this.modal.present();
      },
      sendingToNewContactConfirmation() {
        return new Promise(async (resolve) => {
          await Notifier.showAlert({
            header: this.$t('ncrypt.confirmations.send_message_to_new_contact_title'),
            message: this.$t('ncrypt.confirmations.send_message_to_new_contact_message'),
            buttons: [
              {
                text: this.$t('ncrypt.confirmations.send_message_to_new_contact_send_anyway_button'),
                handler: () => resolve('continue'),
              },
              {
                text: this.$t('ncrypt.confirmations.send_message_to_new_contact_abort_button'),
                handler: () => resolve('abort'),
              },
            ],
          });
        });
      },
      async warnSendingToNewContact() {
        this.loader = await this.$ionic.loadingController.create({
          duration: 1000000,
        });

        await this.loader.present();
        const response = await this.$http
            .post(`/contacts/exists/${this.message.recipient}`);

        await this.loader.dismiss();

        if (response.status !== 200 || !response.data.success) {
          new ApiError(response, true);

          return 'abort';
        }

        if (!response.data.exists) {
          return await this.sendingToNewContactConfirmation();
        }

        return 'continue';
      },
      async send() {
        try {
          const warningAction = await this.warnSendingToNewContact();

          if (warningAction === 'abort') {
            return;
          }

          const loaderMessage = this.$store.getters.user.email_verified_at
              ? this.$t('ncrypt.loader.sending_ncrypt_message_message')
              : this.$t('ncrypt.loader.default_message');

          this.loader = await this.$ionic.loadingController.create({
            duration: 1000000,
            message: loaderMessage,
          });

          await this.loader.present();

          this.message.id = await this.createDraft();

          this.$store.commit(IS_UPLOADING_MUTATION, true);

          await this.$refs.uploader.processQueue(this.message.id);

          this.$store.commit(IS_UPLOADING_MUTATION, false);

          if (!this.$store.getters.user.email_verified_at) {
            await loader.dismiss();

            const flash = {
              message: this.$t('ncrypt.notifications.confirm_email_to_send_message'),
              type: 'primary',
            };

            await this.$store.commit(ADD_FLASH_MESSAGE, flash);

            await this.$router.push({name: 'verify-emailaddress'});
            return;
          }

          if (this.$store.getters.userNeedsToEnable2fa) {
            await loader.dismiss();

            const flash = {
              message: this.$t('ncrypt.notifications.configure_2fa_to_send_message'),
              type: 'primary',
            };

            await this.$store.commit(ADD_FLASH_MESSAGE, flash);

            await this.$router.push({name: 'security'});
            return;
          }

          await this.sendDraft();

          this.message.sent_at = moment().format('HH:mm');

          await this.loader.dismiss();

          await this.messageSent();
        } catch (e) {
          if (this.loader) {
            await this.loader.dismiss();
          }

          await Notifier.showAlert({
            header: this.$t('ncrypt.errors.alert_default_title'),
            message: ErrorHandler.buildApiErrorMessage(e),
            buttons: ['OK'],
          });
        }
      },
      createDraft() {
        return new Promise((resolve, reject) => {
          this.message.notify_sender_on_sent_message = this.notifySelfOnSentMessage ? 1 : 0;

          this.$http
              .post('/drafts/create', {
                message: this.message,
                vaultFiles: this.vaultFiles.map(file => file.id),
              })
              .then(({data}) => {
                if (!data || !data.success) {
                  return reject(data);
                }

                this.message.id = data.id;
                resolve(data.id);
              })
              .catch(error => reject(error));
        });
      },
      sendDraft() {
        return new Promise((resolve, reject) => {
          this.$http
              .post(`/messages/${this.message.id}/send`)
              .then(({data}) => {
                if (!data || !data.success) {
                  return reject(data);
                }
                resolve();
              })
              .catch((error) => reject(error));
        });
      },
      async messageSent() {
        this.ready_to_send = false;

        await Notifier.showToast({
          message: this.$t('ncrypt.notifications.message_was_sent_toast'),
        });

        await this.$store.dispatch('refreshOutbox');

        const flash = {
          message: this.$t('ncrypt.notifications.message_was_sent_flash'),
          type: 'success',
        };

        await this.$store.commit(ADD_FLASH_MESSAGE, flash);

        await this.$router.push({name: 'message', params: {id: this.message.id}});
      },
      updateRecipients() {
        this.message.recipient = this.$refs.updateRecipients.value;

        if (!this.$refs.autocomplete) {
          return;
        }

        this.$refs.autocomplete.value = this.message.recipient;
        this.$refs.autocomplete.display = this.message.recipient;
        if (this.message.recipient.length > 1) {
          this.$refs.autocomplete.search();
        } else {
          this.$refs.autocomplete.clear();
        }
      },
      updateSubject() {
        this.message.subject = this.$refs.updateSubject.value;
      },
      updateBody() {
        this.message.body = this.$refs.updateBody.value;
      },
      updateSender() {
        this.message.sender = this.$refs.sender.value;
      },
      formattedDisplay(result) {
        return `
                    <ion-item lines="none">
                        <ion-avatar slot="start">
                            <img src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA1MTIgNTEyIj48cGF0aCBmaWxsPSIjYzVkYmZmIiBkPSJNMCAwaDUxMnY1MTJIMHoiLz48cGF0aCBkPSJNMjU2IDMwNGM2MS42IDAgMTEyLTUwLjQgMTEyLTExMlMzMTcuNiA4MCAyNTYgODBzLTExMiA1MC40LTExMiAxMTIgNTAuNCAxMTIgMTEyIDExMnptMCA0MGMtNzQuMiAwLTIyNCAzNy44LTIyNCAxMTJ2NTZoNDQ4di01NmMwLTc0LjItMTQ5LjgtMTEyLTIyNC0xMTJ6IiBmaWxsPSIjODJhZWZmIi8+PC9zdmc+"
                                 alt="">
                        </ion-avatar>
                        <ion-label class="ion-no-margin">
                            <ion-item lines="none">
                                <ion-label class="ion-no-margin">
                                    ${this.formatResult(result)}
                                </ion-label>
                            </ion-item>
                        </ion-label>
                    </ion-item>
                `;
      },
      formatResult(result) {
        if (!result.display_name) {
          return `<h2><span outline color="primary">${result.email}</span></h2>`;
        }

        return `
                        <h2><span outline color="primary">${result.display_name}</span></h2>
                        <h4 style="color:#666;">${result.email}</h4>
                    `;
      },
      clearContactResultsDelayed() {
        setTimeout(() => this.clearContactResults(), 500);
      },
      clearContactResults() {
        this.$refs.autocomplete ? this.$refs.autocomplete.clear() : null;
      },
      contactSelected(group) {
        this.message.recipient = group.selectedObject.email;
      },
    }
  }
</script>

<style lang="scss">
  #app ion-card.limitReached {
    ion-card-content {
      pointer-events: none;
    }

    ion-fab-button {
      --background: gray;
    }

    ion-icon {
      background: gray;
    }
  }
</style>
